import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SaveProductButton from './SaveProductButton';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL, getHeaders } from '../config/api';
import slugify from '../utils/slugify';

export default function RecentPriceDrops() {
  const [priceDrops, setPriceDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { authToken } = useAuth();

  useEffect(() => {
    const fetchPriceDrops = async () => {
      setLoading(true);
      setError('');
      try {
        const headers = getHeaders(authToken);
        const response = await fetch(`${API_BASE_URL}/products/recent-price-drops?limit=3`, { headers });
        const data = await response.json();
        if (response.ok) {
          setPriceDrops(Array.isArray(data) ? data : data.data || []);
        } else {
          setError(data.message || 'Failed to fetch recent price drops.');
        }
      } catch (err) {
        console.error('Fetch price drops error:', err);
        setError('An error occurred while fetching recent price drops.');
      }
      setLoading(false);
    };

    fetchPriceDrops();
  }, [authToken]);

  const handleProductSaveStatusChange = (productId, newSavedStatus) => {
    setPriceDrops(prevProducts => 
      prevProducts.map(product => 
        product.id === productId ? { ...product, is_saved_by_user: newSavedStatus } : product
      )
    );
  };

  if (loading) {
    return (
      <section className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold mb-8 text-gray-800 text-center">Recent Price Drops</h2>
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-800 mx-auto"></div>
          <p className="text-gray-500 mt-3">Loading price drops...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold mb-8 text-gray-800 text-center">Recent Price Drops</h2>
        <div className="text-center py-10 bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg shadow-sm max-w-md mx-auto">
          <h3 className="font-semibold text-md mb-1">Could not load price drops.</h3>
          <p className="text-sm">Error: {error}</p>
        </div>
      </section>
    );
  }

  if (priceDrops.length === 0) {
    return (
      <section className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold mb-8 text-gray-800 text-center">Recent Price Drops</h2>
        <div className="text-center py-10 bg-gray-50 border border-gray-200 p-6 rounded-lg shadow-sm max-w-md mx-auto">
           <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>
          <h3 className="mt-2 text-md font-medium text-gray-800">No Price Drops Currently</h3>
          <p className="mt-1 text-sm text-gray-500">We couldn't find any recent price drops. Check again soon!</p>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-5xl mx-auto px-4 py-12">
      <h2 className="text-3xl font-bold mb-8 text-gray-800 text-center">Recent Price Drops</h2>
      <div className="grid md:grid-cols-3 gap-6">
        {priceDrops.map((product) => (
          <div key={product.id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition duration-200 ease-in-out transform hover:-translate-y-1 flex flex-col p-4 text-center">
            <Link 
              to={`/product/${product.id}`} 
              className="flex flex-col items-center flex-grow w-full"
            >
              <img 
                src={product.image_url || product.img || '/placeholder-product.svg'} 
                alt={product.name} 
                className="w-32 h-32 object-cover rounded-lg mb-4 shadow"
              />
              <h3 className="font-semibold text-gray-700 mb-1 h-12 overflow-hidden">{product.name}</h3>
              
              {/* Display price with old price if available */}
              {product.price && product.old_price && parseFloat(product.old_price) > parseFloat(product.price) ? (
                <div className="my-2">
                  <span className="text-gray-400 line-through text-sm mr-2">${parseFloat(product.old_price).toFixed(2)}</span>
                  <span className="text-green-600 font-bold text-xl">${parseFloat(product.price).toFixed(2)}</span>
                  <span className="ml-2 bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                    -{Math.round((1 - parseFloat(product.price)/parseFloat(product.old_price))*100)}%
                  </span>
                </div>
              ) : product.price ? (
                <div className="my-2">
                    <span className="text-green-600 font-bold text-xl">${parseFloat(product.price).toFixed(2)}</span>
                </div>
              ) : null}

            </Link>
            <div className="w-full mt-auto">
                <Link 
                    to={`/product/${product.id}`} 
                    className="block w-full text-center bg-red-800 text-white px-3 py-2 rounded-md hover:bg-red-900 transition text-sm mb-1.5"
                >
                    View Details
                </Link>
                <SaveProductButton 
                    productId={product.id} 
                    initialIsSaved={product.is_saved_by_user || false} 
                    onSaveStatusChange={handleProductSaveStatusChange} 
                />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
} 